import type { QueryClient } from '@tanstack/react-query';
import { offlineCache } from '@/lib/desktop';

/** Query key prefixes worth keeping for an offline session. */
const PERSISTED = ['day-plan', 'tasks', 'clients', 'organization'];

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function shouldPersist(queryKey: readonly unknown[]): boolean {
  return PERSISTED.includes(String(queryKey[0]));
}

/**
 * Restores cached query results at boot and writes fresh ones back.
 *
 * This is deliberately smaller than a full persister: only the screens a
 * manager needs on a plane are kept, and stale entries are dropped rather than
 * shown as if current.
 */
export async function attachOfflinePersistence(client: QueryClient): Promise<void> {
  await Promise.all(
    PERSISTED.map(async (prefix) => {
      const entry = await offlineCache.get<
        Array<{ key: readonly unknown[]; data: unknown }>
      >(`queries:${prefix}`);
      if (!entry) return;
      if (Date.now() - entry.updated_at * 1000 > MAX_AGE_MS) {
        await offlineCache.forget(`queries:${prefix}`);
        return;
      }
      for (const { key, data } of entry.payload ?? []) {
        if (client.getQueryData(key) === undefined) client.setQueryData(key, data);
      }
    }),
  );

  client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.query.state.status !== 'success') return;
    const { queryKey } = event.query;
    if (!shouldPersist(queryKey)) return;

    const prefix = String(queryKey[0]);
    const snapshot = client
      .getQueryCache()
      .findAll({ queryKey: [prefix] })
      .filter((query) => query.state.status === 'success')
      .map((query) => ({ key: query.queryKey, data: query.state.data }));

    void offlineCache.put(`queries:${prefix}`, snapshot);
  });
}
